import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { updateStep, clearValidation } from "../../../redux/profileFormSlice";
import ValidationError from "../../../components/ValidationError/ValidationError";

const schema = yup.object({
  currentSituation: yup
    .array()
    .min(1, "Please select at least one option that describes your current situation"),
});

const options = [
  { field: "employedLookingToUpskill", label: "I'm currently employed and looking to upskill" },
  { field: "consideringCareerPivot", label: "I'm considering a career pivot" },
  { field: "activelyJobSearching", label: "I'm actively job searching" },
  { field: "studentOrGraduate", label: "I'm a student or recent graduate" },
  { field: "other", label: "Other" },
];

const CurrentSituation = () => {
  const dispatch = useDispatch();
  const initialOptions =
    useSelector((state) => state.profileForm.step1?.currentSituation) || [];
  const shouldValidate = useSelector((state) => state.profileForm.shouldValidate);
  const validationStep = useSelector((state) => state.profileForm.validationStep);

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { currentSituation: initialOptions },
  });

  const selectedOptions = watch("currentSituation");

  useEffect(() => {
    dispatch(updateStep({ step: "step1", data: { currentSituation: selectedOptions } }));
  }, [selectedOptions, dispatch]);

  // Handle validation trigger from Redux
  useEffect(() => {
    if (shouldValidate && validationStep === 1) {
      trigger("currentSituation");
      dispatch(clearValidation());
    }
  }, [shouldValidate, validationStep, trigger, dispatch]);

  const toggleOption = (option) => {
    const newOptions = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option];

    setValue("currentSituation", newOptions, { shouldValidate: true });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">Current Situation</h2>
        <h3 className="text-lg sm:text-xl font-medium text-gray-800 mb-2">
          What best describes your current situation?
        </h3>
      </div>
      <ValidationError message={errors.currentSituation?.message} />
      <div className="space-y-4">
        {options.map(({ field, label }) => (
          <label
            key={field}
            className="flex items-center space-x-3 cursor-pointer border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedOptions.includes(field)}
              onChange={() => toggleOption(field)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <span className="text-gray-800 font-medium">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default CurrentSituation;
